(() => {
  'use strict';

  if (window.TSSEmptyCartCleanupInitialised) {
    return;
  }

  window.TSSEmptyCartCleanupInitialised = true;

  const shopRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  const orderAttributeKeys = [
    'Order fulfilment method',
    'Delivery address type',
    'Authority to leave',
    'Authority to leave location',
    'Building signage',
    'Supplier delivery instructions',
    'Delivery information complete',
    'Pickup confirmation',
    'Pickup information complete'
  ];

  let scheduledCheck = null;
  let cleanupPromise = null;

  function wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });
  }

  async function retrieveCart() {
    const response = await fetch(
      `${shopRoot}cart.js`,
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to retrieve cart: ${response.status}`
      );
    }

    return response.json();
  }

  function cartContainsSavedOrderInformation(cart) {
    const attributes =
      cart && cart.attributes
        ? cart.attributes
        : {};

    const containsOrderAttribute =
      orderAttributeKeys.some((key) => {
        return Boolean(attributes[key]);
      });

    const containsCartNote =
      Boolean(cart && cart.note);

    return (
      containsOrderAttribute ||
      containsCartNote
    );
  }

  async function clearSavedOrderInformation() {
    const attributes = {};

    orderAttributeKeys.forEach((key) => {
      attributes[key] = '';
    });

    const response = await fetch(
      `${shopRoot}cart/update.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          /*
           * Clear any old native Shopify cart note,
           * including the previous TEST ATL value.
           */
          note: '',
          attributes
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to clear order information: ${response.status}`
      );
    }

    return response.json();
  }

  async function confirmEmptyCartAndClean() {
    if (cleanupPromise) {
      return cleanupPromise;
    }

    cleanupPromise = (async () => {
      /*
       * Check twice to avoid acting on a temporarily
       * stale cart response immediately after a change.
       */
      const firstCart = await retrieveCart();

      if (Number(firstCart.item_count) !== 0) {
        return;
      }

      await wait(650);

      const secondCart = await retrieveCart();

      if (Number(secondCart.item_count) !== 0) {
        return;
      }

      if (
        !cartContainsSavedOrderInformation(
          secondCart
        )
      ) {
        return;
      }

      await clearSavedOrderInformation();

      document.dispatchEvent(
        new CustomEvent(
          'tss:empty-cart-information-cleared'
        )
      );
    })()
      .catch((error) => {
        console.warn(
          'Empty-cart order information could not be cleared.',
          error
        );
      })
      .finally(() => {
        cleanupPromise = null;
      });

    return cleanupPromise;
  }

  function scheduleCleanupCheck(
    delay = 700
  ) {
    window.clearTimeout(scheduledCheck);

    scheduledCheck = window.setTimeout(
      confirmEmptyCartAndClean,
      delay
    );
  }

  /*
   * Check when the page first loads.
   */
  function initialiseCleanup() {
    scheduleCleanupCheck(700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseCleanup,
      { once: true }
    );
  } else {
    initialiseCleanup();
  }

  /*
   * Recheck when returning to a previously cached page.
   */
  window.addEventListener(
    'pageshow',
    () => {
      scheduleCleanupCheck(500);
    }
  );

  /*
   * This custom event is already emitted by
   * tss-order-information.js when cart lines change.
   */
  document.addEventListener(
    'tss:order-information-reset',
    () => {
      scheduleCleanupCheck(900);
    }
  );

  /*
   * Also listen for Shopify's standard cart-line event.
   */
  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      if (
        event.promise &&
        typeof event.promise.then ===
          'function'
      ) {
        Promise.resolve(event.promise)
          .then(() => {
            scheduleCleanupCheck(350);
          })
          .catch(() => {
            /*
             * Do nothing when the cart change failed.
             */
          });

        return;
      }

      scheduleCleanupCheck(900);
    }
  );
})();